import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { ProductoApiService } from '../services/producto-api.service';
import { ProductoAttachmentApiService } from '../services/producto-attachment-api.service';
import { ProductoTierPriceApiService } from '../services/producto-tier-price-api.service';
import { ProductoPriceCurrencyApiService } from '../services/producto-price-currency-api.service';
import { ProductoVariantAttributeApiService } from '../services/producto-variant-attribute-api.service';
import { ProductoVariantApiService } from '../services/producto-variant-api.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { AuditApiService } from '../../audit/services/audit-api.service';
import { AuditLogResponse } from '../../audit/models/audit.model';
import { AuditDisplayService } from '../../../shared/services/audit-display.service';
import {
  ProductResponse,
  ProductPriceHistoryResponse,
} from '../models/producto.model';
import { ProductCategoryResponse } from '../models/categoria-producto.model';
import { ProductAttachmentResponse } from '../models/producto-attachment.model';
import { ProductTierPriceResponse } from '../models/producto-tier-price.model';
import { ProductPriceCurrencyResponse } from '../models/producto-price-currency.model';
import { ProductVariantAttributeResponse } from '../models/producto-variant-attribute.model';
import { ProductVariantResponse } from '../models/producto-variant.model';
import { ProductoInternalNotesComponent } from '../producto-internal-notes/producto-internal-notes.component';
import { AuditTimelineComponent } from '../../../shared/components/audit/audit-timeline.component';
import { formatMoney } from '../../../shared/utils/currency-format.utils';
import {
  ATTACHMENT_TYPE_LABELS,
  BILLING_CONDITION_LABELS,
  BILLING_CYCLE_LABELS,
  PRICE_CURRENCY_TYPE_LABELS,
  PRICING_METHOD_LABELS,
  PRODUCT_TYPE_LABELS,
  productoLabel,
} from '../models/producto-labels';

type BadgeColor =
  | 'primary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'light'
  | 'dark';
type ProductTab =
  | 'info'
  | 'attachments'
  | 'tierPrices'
  | 'priceCurrencies'
  | 'variantAttributes'
  | 'variants'
  | 'internalNotes'
  | 'timeline';

@Component({
  selector: 'app-producto-detail-modal',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    BadgeComponent,
    ProductoInternalNotesComponent,
    AuditTimelineComponent,
  ],
  templateUrl: './producto-detail-modal.component.html',
})
export class ProductoDetailModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() producto: ProductResponse | null = null;
  @Input() categorias: ProductCategoryResponse[] = [];
  @Output() close = new EventEmitter<void>();

  priceHistory: ProductPriceHistoryResponse[] = [];
  loadingHistory = false;

  expandedLogIds = new Set<number>();

  businessId = 1;
  /** Inc 58: error real de la carga de adjuntos (antes se tragaba y parecía "Sin adjuntos"). */
  attachmentsError = '';

  // Audit timeline
  activeTab: ProductTab = 'info';
  timelineLogs: AuditLogResponse[] = [];
  loadingTimeline = false;
  timelineError = '';
  timelinePage = 0;
  timelineTotalPages = 0;
  timelineTotalElements = 0;

  // Lazy sub-entity sections
  showAttachments = false;
  showVariants = false;
  showVariantAttributes = false;
  showTierPrices = false;
  showPriceCurrencies = false;

  attachments: ProductAttachmentResponse[] = [];
  loadingAttachments = false;
  variants: ProductVariantResponse[] = [];
  loadingVariants = false;
  variantAttributes: ProductVariantAttributeResponse[] = [];
  loadingVariantAttributes = false;
  tierPrices: ProductTierPriceResponse[] = [];
  loadingTierPrices = false;
  priceCurrencies: ProductPriceCurrencyResponse[] = [];
  loadingPriceCurrencies = false;

  constructor(
    private productApi: ProductoApiService,
    private attachmentApi: ProductoAttachmentApiService,
    private tierPriceApi: ProductoTierPriceApiService,
    private priceCurrencyApi: ProductoPriceCurrencyApiService,
    private variantAttrApi: ProductoVariantAttributeApiService,
    private variantApi: ProductoVariantApiService,
    private authService: AuthService,
    private datetimeService: DatetimeService,
    private auditApi: AuditApiService,
    private auditDisplay: AuditDisplayService,
  ) {
    const user = this.authService.getCurrentUser();
    this.businessId = user?.businessId || 1;
  }

  /**
   * Inc 58: reacciona también a un cambio de `producto` con el modal ya abierto y
   * resetea TODO el estado (incluidos los flags de carga). Antes solo miraba
   * `isOpen`, así que si una petición previa dejaba `loadingAttachments = true`
   * la pestaña Adjuntos quedaba vacía para siempre.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!(changes['isOpen'] || changes['producto'])) return;
    if (!this.isOpen || !this.producto) return;

    // Inc 58: businessId real del usuario (el `|| 1` provocaba 403 en otros negocios).
    this.businessId = this.authService.getBusinessId() ?? this.businessId;

    this.activeTab = 'info';
    this.priceHistory = [];
    this.timelineLogs = [];
    this.timelinePage = 0;
    this.timelineTotalPages = 0;
    this.timelineError = '';
    this.loadingTimeline = false;
    this.expandedLogIds.clear();
    this.attachments = [];
    this.attachmentsError = '';
    this.loadingAttachments = false;
    this.variants = [];
    this.loadingVariants = false;
    this.variantAttributes = [];
    this.loadingVariantAttributes = false;
    this.tierPrices = [];
    this.loadingTierPrices = false;
    this.priceCurrencies = [];
    this.loadingPriceCurrencies = false;
    this.cargarHistorialPrecios();
  }

  /**
   * Inc 58: sin guarda de caché sobre `attachments.length` — el mismo endpoint que
   * usa el formulario, y el error se muestra en vez de simularse "Sin adjuntos".
   */
  private loadAttachments(): void {
    if (this.loadingAttachments || !this.producto) return;
    this.loadingAttachments = true;
    this.attachmentsError = '';
    this.attachmentApi
      .searchProductAttachment({
        businessId: this.businessId,
        productId: this.producto.id,
        size: 50,
      })
      .subscribe({
        next: (res) => {
          this.attachments = res.content;
          this.loadingAttachments = false;
        },
        error: (err) => {
          this.attachments = [];
          this.attachmentsError =
            err?.error?.message ||
            'No se pudieron cargar los adjuntos del producto.';
          this.loadingAttachments = false;
        },
      });
  }

  private loadVariants(): void {
    if (this.variants.length > 0 || this.loadingVariants || !this.producto)
      return;
    this.loadingVariants = true;
    this.variantApi.getProductVariants(this.producto.id).subscribe({
      next: (data) => {
        this.variants = data;
        this.loadingVariants = false;
      },
      error: () => {
        this.variants = [];
        this.loadingVariants = false;
      },
    });
  }

  private loadVariantAttributes(): void {
    if (
      this.variantAttributes.length > 0 ||
      this.loadingVariantAttributes ||
      !this.producto
    )
      return;
    this.loadingVariantAttributes = true;
    this.variantAttrApi
      .getProductVariantAttributes(this.producto.id)
      .subscribe({
        next: (data) => {
          this.variantAttributes = data;
          this.loadingVariantAttributes = false;
        },
        error: () => {
          this.variantAttributes = [];
          this.loadingVariantAttributes = false;
        },
      });
  }

  private loadTierPrices(): void {
    if (this.tierPrices.length > 0 || this.loadingTierPrices || !this.producto)
      return;
    this.loadingTierPrices = true;
    this.tierPriceApi.getProductTierPrices(this.producto.id).subscribe({
      next: (data) => {
        this.tierPrices = data;
        this.loadingTierPrices = false;
      },
      error: () => {
        this.tierPrices = [];
        this.loadingTierPrices = false;
      },
    });
  }

  private loadPriceCurrencies(): void {
    if (
      this.priceCurrencies.length > 0 ||
      this.loadingPriceCurrencies ||
      !this.producto
    )
      return;
    this.loadingPriceCurrencies = true;
    this.priceCurrencyApi
      .getProductPriceCurrencies(this.producto.id)
      .subscribe({
        next: (data) => {
          this.priceCurrencies = data;
          this.loadingPriceCurrencies = false;
        },
        error: () => {
          this.priceCurrencies = [];
          this.loadingPriceCurrencies = false;
        },
      });
  }

  private cargarHistorialPrecios(): void {
    if (!this.producto) return;
    this.loadingHistory = true;
    this.productApi.getPriceHistory(this.producto.id, 0, 5).subscribe({
      next: (response) => {
        this.priceHistory = response.content;
        this.loadingHistory = false;
      },
      error: () => {
        this.priceHistory = [];
        this.loadingHistory = false;
      },
    });
  }

  onTabChange(tab: ProductTab): void {
    this.activeTab = tab;
    switch (tab) {
      case 'timeline':
        if (
          tab === 'timeline' &&
          this.timelineLogs.length === 0 &&
          !this.loadingTimeline
        ) {
          this.loadTimeline(0);
        }
        break;
      case 'attachments':
        this.loadAttachments();
        break;
      case 'tierPrices':
        this.loadTierPrices();
        break;
      case 'priceCurrencies':
        this.loadPriceCurrencies();
        break;
      case 'variantAttributes':
        this.loadVariantAttributes();
        break;
      case 'variants':
        this.loadVariants();
        break;
    }
  }

  loadTimeline(page = 0): void {
    if (!this.producto?.id) return;

    this.loadingTimeline = true;
    this.timelineError = '';
    this.timelinePage = page;

    this.auditApi
      .getLogs({
        entityName: 'product',
        idEntity: String(this.producto.id),
        page,
        size: 15,
      })
      .subscribe({
        next: (data) => {
          this.timelineLogs = data.content ?? [];
          this.timelinePage = data.page ?? page;
          this.timelineTotalPages = data.totalPages ?? 1;
          this.timelineTotalElements = data.totalElements ?? 0;
          this.loadingTimeline = false;
        },
        error: () => {
          this.timelineError = 'Error al cargar la línea de tiempo.';
          this.loadingTimeline = false;
        },
      });
  }

  /** Inc 27: true si el adjunto es un archivo subido al storage (fileUrl = id numérico). */
  esArchivoSubido(a: ProductAttachmentResponse): boolean {
    return /^\d+$/.test((a.fileUrl || '').trim());
  }

  /**
   * Inc 27: descarga por storageRef (blob) igual que la pantalla editable; para
   * adjuntos por enlace externo abre la URL directa en otra pestaña.
   */
  descargarAdjunto(a: ProductAttachmentResponse): void {
    if (!this.esArchivoSubido(a)) {
      window.open(a.fileUrl, '_blank');
      return;
    }
    this.attachmentApi.downloadDocument(Number(a.fileUrl)).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = a.filename || 'documento';
        link.click();
        URL.revokeObjectURL(url);
      },
      error: () => {
        /* descarga fallida: se mantiene el modal sin acción destructiva */
      },
    });
  }

  toggleLog(id: number): void {
    if (this.expandedLogIds.has(id)) this.expandedLogIds.delete(id);
    else this.expandedLogIds.add(id);
  }

  isLogExpanded(id: number): boolean {
    return this.expandedLogIds.has(id);
  }

  getLogDotColor(op: string): string {
    switch (op?.toUpperCase()) {
      case 'CREATE':
        return 'bg-success-500';
      case 'UPDATE':
        return 'bg-brand-500';
      case 'DELETE':
        return 'bg-error-500';
      default:
        return 'bg-gray-400';
    }
  }

  onClose(): void {
    this.close.emit();
  }

  getCategoryName(): string {
    if (!this.producto) return 'Sin categoría';
    if (this.producto.categoryName) return this.producto.categoryName;
    if (!this.producto.idCategory) return 'Sin categoría';
    const cat = this.categorias.find((c) => c.id === this.producto!.idCategory);
    return cat?.name || 'Sin categoría';
  }

  /** Inc 53: tipo de primer nivel — Producto | Servicio. */
  getTipoLabel(): string {
    return this.producto?.type === 'service' ? 'Servicio' : 'Producto';
  }
  getTipoColor(): BadgeColor {
    return this.producto?.type === 'service' ? 'warning' : 'primary';
  }

  /**
   * Inc 53: subtipo de segundo nivel. Para servicios sale del campo `subtype`
   * ("resources"/"consulting"); para productos, del propio `type`.
   */
  getSubtipoLabel(): string {
    if (!this.producto) return '-';
    if (this.producto.type === 'service') {
      if (this.producto.subtype === 'resources') return 'Recursos';
      if (this.producto.subtype === 'consulting') return 'Consultoría';
      return 'Sin especificar';
    }
    return this.producto.type === 'no_inventory'
      ? 'Sin inventario'
      : 'Con inventario';
  }

  // ── Inc 57: vigencia promocional ──

  /** ¿El producto tiene alguna promoción configurada (fechas o precio)? */
  get tienePromocion(): boolean {
    const p = this.producto;
    if (!p) return false;
    return !!(
      p.promoValidFrom ||
      p.promoValidUntil ||
      (p.promoPrice !== undefined && p.promoPrice !== null)
    );
  }

  /** Etiqueta del `promoStatus` del backend (4 valores exactos). */
  getPromoStatusLabel(): string {
    switch (this.producto?.promoStatus) {
      case 'active':
        return 'Activa';
      case 'scheduled':
        return 'Programada';
      case 'expired':
        return 'Expirada';
      default:
        return 'Inactiva';
    }
  }

  getPromoStatusColor(): BadgeColor {
    switch (this.producto?.promoStatus) {
      case 'active':
        return 'success';
      case 'scheduled':
        return 'warning';
      case 'expired':
        return 'error';
      default:
        return 'light';
    }
  }

  /** Fecha sin hora ('YYYY-MM-DD' → 'dd/mm/yyyy'), para las vigencias. */
  formatDateOnly(dateStr?: string): string {
    if (!dateStr) return '-';
    const partes = dateStr.split('T')[0].split('-');
    if (partes.length === 3) {
      const [y, m, d] = partes;
      return `${d}/${m}/${y}`;
    }
    return this.datetimeService.formatDateTime(dateStr);
  }

  getActiveColor(): BadgeColor {
    return this.producto?.isActive ? 'success' : 'error';
  }
  getActiveLabel(): string {
    return this.producto?.isActive ? 'Activo' : 'Inactivo';
  }

  /**
   * Chapa de la cabecera. Se deriva del `promoStatus` del backend, que es el
   * autoritativo: las banderas `promoExpired / promoNotYetStarted / promoVigente`
   * describen solo la VENTANA de fechas, así que una vigencia sin precio
   * promocional (o con el interruptor apagado) rotulaba "Vigente" una promoción
   * que no existe.
   */
  get promoStatus() {
    if (!this.producto) return;
    switch (this.producto.promoStatus) {
      case 'expired':
        return { text: 'Expirada', color: 'error' as BadgeColor };
      case 'scheduled':
        return { text: 'Programada', color: 'warning' as BadgeColor };
      case 'active':
        return { text: 'Vigente', color: 'success' as BadgeColor };
      default:
        return null;
    }
  }

  /**
   * ¿Se debe mostrar el precio promocional en lugar del de lista? Solo cuando la
   * promoción está VIGENTE, ENCENDIDA y además tiene precio promocional.
   * `promoVigente` solo mira las fechas: con la vigencia cargada pero sin
   * `promoPrice` (o con el interruptor apagado) se tachaba el precio real y se
   * mostraba "-" como precio actual.
   */
  get promoActiva(): boolean {
    const p = this.producto;
    return !!(
      p &&
      p.promoVigente &&
      p.promoEnabled !== false &&
      p.promoPrice !== undefined &&
      p.promoPrice !== null
    );
  }

  formatPrecio(price?: number, prefix?: string, currencyCode?: string): string {
    if (price === undefined || price === null) return '-';
    // La rama del prefijo formateaba a 2 decimales fijos e ignoraba la moneda:
    // en yenes salía "¥ 1.560,25" y en dinares se comía el tercer decimal.
    // formatMoney usa la unidad mínima ISO 4217 del código.
    if (prefix) {
      return formatMoney(price, prefix, currencyCode);
    }
    return currencyCode
      ? new Intl.NumberFormat('es-MX', {
          style: 'currency',
          currency: currencyCode,
        }).format(price)
      : formatMoney(price, null, null);
  }

  formatDate(dateString?: string): string {
    return this.datetimeService.formatDateTime(dateString);
  }

  formatDateShort(dateString?: string): string {
    return this.datetimeService.formatDateShort(dateString);
  }

  getUserLabel(log: AuditLogResponse): string {
    try {
      if (log.userFullName) return log.userFullName;
      if (log.username) return log.username;
      return `Usuario #${log.idUser}`;
    } catch {
      return 'Usuario desconocido';
    }
  }

  private readonly FIELD_LABELS: Record<string, string> = {
    name: 'Nombre',
    description: 'Descripción',
    price: 'Precio',
    cost: 'Costo',
    sku: 'SKU',
    isActive: 'Estado activo',
    type: 'Tipo',
    currencyId: 'Moneda',
    currencyCode: 'Código moneda',
    prefix: 'Prefijo moneda',
    taxPercent: 'Impuesto %',
    isTaxable: 'Gravable',
    maxDiscountPercent: 'Descuento máx.',
    minPrice: 'Precio mínimo',
    stockQuantity: 'Stock',
    minStockLevel: 'Stock mínimo',
    isTrackable: 'Rastreable',
    unitOfMeasure: 'Unidad de medida',
    isRecurring: 'Recurrente',
    billingCycle: 'Ciclo de facturación',
    billingCondition: 'Condición de facturación',
    durationHours: 'Horas de servicio',
    margin: 'Margen',
    imageUrl: 'Imagen',
    idCategory: 'Categoría',
  };

  // Campos técnicos que NO mostrar al usuario
  private readonly HIDDEN_FIELDS = [
    'id',
    'ref',
    'businessId',
    'idBusiness',
    'datetimeCreated',
    'datetimeUpdated',
    'skuPrefix',
    'hasVariants',
    'usesTierPricing',
    'currencyId',
  ];

  getFieldLabel(field: string): string {
    return this.FIELD_LABELS[field] || field;
  }

  getChangedValues(
    log: AuditLogResponse,
  ): Array<{ field: string; label: string; old: string; new: string }> {
    // Preferir el enriquecimiento del backend (changesDisplay); si es null, fallback técnico propio.
    const enriched = this.auditDisplay.displayRows(log);
    if (enriched !== null) {
      return enriched.map((r) => ({
        field: r.field,
        label: r.label,
        old: r.oldVal,
        new: r.newVal,
      }));
    }
    try {
      const oldObj =
        typeof log.oldValues === 'string'
          ? JSON.parse(log.oldValues)
          : log.oldValues || {};
      const newObj =
        typeof log.newValues === 'string'
          ? JSON.parse(log.newValues)
          : log.newValues || {};

      const fields: string[] =
        Array.isArray(log.changedFields) && log.changedFields.length > 0
          ? log.changedFields
          : Object.keys(newObj).filter(
              (k) => JSON.stringify(oldObj[k]) !== JSON.stringify(newObj[k]),
            );

      return fields
        .filter((f) => !this.HIDDEN_FIELDS.includes(f))
        .map((f) => ({
          field: f,
          label: this.getFieldLabel(f),
          old: this.formatFieldValue(f, oldObj[f]),
          new: this.formatFieldValue(f, newObj[f]),
        }))
        .filter((f) => f.old !== f.new);
    } catch {
      return [];
    }
  }

  // Campos que son booleanos → "Sí" / "No"
  private readonly BOOLEAN_FIELDS = [
    'isActive',
    'isTaxable',
    'isRecurring',
    'isTrackable',
    'usesTierPricing',
    'hasVariants',
  ];

  // Campos de porcentaje → se les añade "%"
  private readonly PERCENT_FIELDS = [
    'taxPercent',
    'maxDiscountPercent',
    'margin',
  ];

  // Campos monetarios → se formatean con formatPrecio()
  private readonly MONEY_FIELDS = ['price', 'cost', 'minPrice'];

  // Diccionarios compartidos del módulo (models/producto-labels.ts)
  private readonly TYPE_LABELS = PRODUCT_TYPE_LABELS;
  private readonly BILLING_CYCLE_LABELS = BILLING_CYCLE_LABELS;
  private readonly BILLING_CONDITION_LABELS = BILLING_CONDITION_LABELS;

  attachmentTypeLabel(value: string | null | undefined): string {
    return productoLabel(ATTACHMENT_TYPE_LABELS, value);
  }

  pricingMethodLabel(value: string | null | undefined): string {
    return productoLabel(PRICING_METHOD_LABELS, value);
  }

  priceCurrencyTypeLabel(value: string | null | undefined): string {
    return productoLabel(PRICE_CURRENCY_TYPE_LABELS, value);
  }

  formatFieldValue(field: string, value: unknown): string {
    if (value === undefined || value === null || value === '') return '-';

    // Booleanos → Sí / No
    if (this.BOOLEAN_FIELDS.includes(field) || typeof value === 'boolean') {
      return value === true || value === 'true' ? 'Sí' : 'No';
    }

    // Enums conocidos
    if (field === 'type')
      return this.TYPE_LABELS[String(value)] ?? this.prettify(String(value));
    if (field === 'billingCycle')
      return (
        this.BILLING_CYCLE_LABELS[String(value)] ?? this.prettify(String(value))
      );
    if (field === 'billingCondition')
      return (
        this.BILLING_CONDITION_LABELS[String(value)] ??
        this.prettify(String(value))
      );

    // Categoría: ID → nombre
    if (field === 'idCategory') {
      const cat = this.categorias.find((c) => c.id === Number(value));
      return cat?.name ?? `Categoría #${value}`;
    }

    // Porcentajes
    if (this.PERCENT_FIELDS.includes(field)) return `${value}%`;

    // Montos
    if (this.MONEY_FIELDS.includes(field))
      return this.formatPrecio(Number(value));

    return String(value);
  }

  // Convierte snake_case desconocido en texto legible: "on_hold" → "On Hold"
  private prettify(value: string): string {
    return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  //
  readonly tabs: { id: ProductTab; label: string }[] = [
    { id: 'info', label: 'Información' },
    { id: 'attachments', label: 'Adjuntos' },
    { id: 'tierPrices', label: 'Escalonados' },
    { id: 'priceCurrencies', label: 'Por moneda' },
    { id: 'variantAttributes', label: 'Atributos' },
    { id: 'variants', label: 'Variantes' },
    { id: 'internalNotes', label: 'Notas internas' },
    { id: 'timeline', label: 'Línea de tiempo' },
  ];
}
